import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import './RewardSpinner.css';

/**
 * SpinLeadForm - Lead capture form before spinning
 * 
 * Similar to PerformanceReportForm but for spinner.
 * Captures: Name, Email, Phone (with +91 prefix)
 */
const SpinLeadForm = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    // Clean phone number (remove non-digits)
    const cleanedPhone = formData.phone.replace(/\D/g, '');
    
    if (!cleanedPhone) {
      newErrors.phone = 'Phone number is required';
    } else if (cleanedPhone.length !== 10) {
      newErrors.phone = 'Please enter exactly 10 digits';
    } else if (!/^[6-9]/.test(cleanedPhone)) {
      newErrors.phone = 'Please enter a valid Indian mobile number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow digits and limit to 10
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    // Clear submit error when user types
    if (submitError) {
      setSubmitError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const result = await onSubmit(formData);
      if (!result.success) {
        setSubmitError(result.error);
      }
    } catch (err) {
      setSubmitError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lead-form-overlay">
      <div className="lead-form-modal">
        <button className="lead-form-close" onClick={onClose}>
          <X size={20} />
        </button>
        
        <div className="lead-form-header">
          <h3>🎯 Almost There!</h3>
          <p>Enter your details to spin the wheel</p>
        </div>
        
        <form onSubmit={handleSubmit} className="lead-form">
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              className={errors.name ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.name && <span className="error-text">{errors.name}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className={errors.email ? 'error' : ''}
              disabled={isSubmitting}
            />
            {errors.email && <span className="error-text">{errors.email}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <div className="phone-input-wrapper">
              <span className="country-code">+91</span>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
                className={`phone-input ${errors.phone ? 'error' : ''}`}
                disabled={isSubmitting}
                maxLength={10}
                inputMode="numeric"
              />
            </div>
            {errors.phone && <span className="error-text">{errors.phone}</span>}
          </div>
          
          {submitError && (
            <div className="submit-error">
              {submitError}
            </div>
          )}
          
          <button 
            type="submit" 
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <span>🎰</span>
                <span>Spin Now!</span>
              </>
            )}
          </button>
        </form>
        
        <p className="form-disclaimer">
          By submitting, you agree to receive promotional communications.
        </p>
      </div>
    </div>
  );
};

export default SpinLeadForm;
